/**
 * THE MINIMISED ASK AI LAUNCHER — a calm pill that, when you reach for it,
 * answers "what do I even ask it?" before you have to commit to opening
 * anything.
 *
 * WHY IT LEADS WITH CAPABILITY RATHER THAN POLISH. The pill is not the
 * assistant's default state: `DEFAULT_MODE` is 'compact', so the card is open on
 * a first visit and the pill only appears once somebody has pressed ✕ — and
 * `chatWidget:mode` keeps them there forever. Whatever sits in this corner is
 * therefore talking to someone who already dismissed the assistant once.
 * Measured 19 Oct – 1 Nov 2026 (14 days, the whole of CloudWatch's retention on
 * the agent runtime): 116 human turns, 65 of them on 1 Nov alone, so the
 * thirteen days before that are 51 turns — 3.9 a day for the entire company,
 * with two days at zero. That is not a button-colour problem, it is "nobody
 * knows what to ask", so the pill's job is to show three real questions.
 *
 * WHAT MOVES, AND WHEN IT DOESN'T. Idle is completely still: no badge, no
 * pulse, no loop, and the 1px hover lift the old pill had is gone (on a 44px
 * target it reads as a wobble). The question card fades up on hover or keyboard
 * focus, over 160ms, once. The questions are shuffled ONCE when this component
 * mounts and never again — not on a timer, because a thing that rearranges
 * itself in the corner of a screen people work in all day gets muted or
 * resented. Under `prefers-reduced-motion` the fade becomes a cut (App.scss).
 *
 * TOUCH. Hover is invisible to a finger, so where `(hover: none)` holds the pill
 * itself opens the card on the first tap and a question on the second; the
 * card's last row is a real button, so there is still a way to open the
 * assistant empty.
 *
 * Contextual content: `useActiveAskAiSuggestion` — see askAiSuggestions.jsx for
 * what a page may register and when. Firing a question goes through `askAI`
 * from AskAIButton.jsx unchanged, which already opens the widget expanded on a
 * fresh session with the text sent as its first message.
 */
import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { askAI } from './AskAIButton';
import {
  GLOBAL_SUGGESTIONS, MAX_SUGGESTIONS, useActiveAskAiSuggestion,
} from './askAiSuggestions';

/** Fingers get an explicit open state; pointers get hover. */
const HOVERLESS_MEDIA_QUERY = '(hover: none)';

function shuffled(list) {
  const out = [...list];
  for (let i = out.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}

function useHoverless() {
  const [hoverless, setHoverless] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia(HOVERLESS_MEDIA_QUERY);
    const sync = () => setHoverless(mq.matches);
    sync();
    // Safari <14 only supports addListener.
    if (mq.addEventListener) mq.addEventListener('change', sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', sync);
      else mq.removeListener(sync);
    };
  }, []);
  return hoverless;
}

function AskAiLauncher({ onOpen }) {
  const pageSuggestion = useActiveAskAiSuggestion();
  const hoverless = useHoverless();
  const [tapOpen, setTapOpen] = useState(false);
  const rootRef = useRef(null);

  // Shuffled once per mount — see "WHAT MOVES" above. The dependency list is
  // deliberately empty: re-shuffling when the page's suggestion changes would
  // rearrange the two fallback rows under someone navigating, for no gain.
  const pool = useMemo(() => shuffled(GLOBAL_SUGGESTIONS), []);

  // The page's question leads; the global list fills the rest. Compared on the
  // question text so a page that registers one of the global five (a reasonable
  // thing to do — it is still the most relevant one there) does not show it
  // twice.
  const questions = useMemo(() => {
    const chosen = pageSuggestion ? [pageSuggestion] : [];
    pool.forEach((question) => {
      if (chosen.length >= MAX_SUGGESTIONS) return;
      if (chosen.some((c) => c.question === question)) return;
      chosen.push({ question, prompt: question });
    });
    return chosen;
  }, [pageSuggestion, pool]);

  // Escape and a tap outside close the touch-opened card. No-ops while it is
  // shut, so this costs nothing on the desktop path.
  useEffect(() => {
    if (!tapOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setTapOpen(false); };
    const onOutside = (e) => {
      if (!rootRef.current?.contains(e.target)) setTapOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onOutside);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onOutside);
    };
  }, [tapOpen]);

  const open = () => { setTapOpen(false); onOpen(); };

  const onPillClick = () => {
    // Where there is no hover, the first tap has to be the one that reveals the
    // questions — otherwise they are an affordance a finger can never find.
    if (hoverless && !tapOpen) {
      setTapOpen(true);
      return;
    }
    open();
  };

  return (
    <div
      className={`chat-widget-launcher${tapOpen ? ' chat-widget-launcher-open' : ''}`}
      ref={rootRef}
    >
      {/*
        The pill comes FIRST in the DOM and the card is drawn above it in CSS.
        Visual order and source order disagree on purpose: the card is only
        reachable once the pill has focus, so Tab → pill → its questions is the
        order someone actually travels in. The card is `visibility: hidden` when
        shut rather than merely transparent, so those buttons are out of the tab
        order entirely until it opens.
      */}
      <button
        type="button"
        className="chat-widget-fab"
        onClick={onPillClick}
        title="Assistant"
        aria-expanded={hoverless ? tapOpen : undefined}
      >
        <svg
          className="chat-widget-fab-icon"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.7"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="M21 11.5a8.5 8.5 0 0 1-12.1 7.7L4 21l1.8-4.9A8.5 8.5 0 1 1 21 11.5z" />
          <path d="M8.5 11.5h7M8.5 8.5h4" />
        </svg>
        {/*
          The pill's accessible name is its visible text, so a page's own label
          is what a screen reader announces too. It used to be a fixed
          `aria-label="Open assistant chat"` over a visible "Ask AI", which is
          the WCAG 2.5.3 "Label in Name" failure — voice-control users could not
          say what they could see.
        */}
        <span className="chat-widget-fab-label">{pageSuggestion?.label || 'Ask AI'}</span>
      </button>

      <div
        className="chat-widget-suggestions"
        role="group"
        aria-label={pageSuggestion ? 'Questions about this page' : 'Example questions'}
      >
        <div className="chat-widget-suggestions-cap">
          {pageSuggestion ? 'On this page' : 'Try asking'}
        </div>
        {questions.map((q) => (
          <button
            key={q.question}
            type="button"
            className="chat-widget-suggestion"
            onClick={() => { setTapOpen(false); askAI(q.prompt); }}
          >
            {q.question}
            <span className="chat-widget-suggestion-arrow" aria-hidden="true">→</span>
          </button>
        ))}
        <button type="button" className="chat-widget-suggestions-foot" onClick={open}>
          …or ask anything else
        </button>
      </div>
    </div>
  );
}

export default AskAiLauncher;
